/**
 * Hook do zarządzania logiką akcji tabeli
 * Zwraca tylko dane i metody - renderowanie jest w komponencie TableTopToolbar
 */

import { useMemo } from 'react';
import { IWiseTableAction } from 'components/wise/types';
import { useCallCommand } from 'plugins/api';

interface IUseWiseTableActionsParams {
  actions: IWiseTableAction[];
  refetchTableData: () => void;
}

interface IUseWiseTableActionsReturn {
  noContextActions: IWiseTableAction[];
  mainActions: IWiseTableAction[];
  additionalActions: IWiseTableAction[];
  openActions: IWiseTableAction[];
  columnActions: IWiseTableAction[];
  handleReorder: (data: {
    entityId: number;
    beforeEntityId: number | null;
    afterEntityId: number | null;
  }) => void;
  isReordering: boolean;
  hasReorderAction: boolean;
}

/**
 * Hook do zarządzania logiką akcji
 */
export const useWiseTableActions = ({
  actions,
  refetchTableData
}: IUseWiseTableActionsParams): IUseWiseTableActionsReturn => {
  const noContextActions = useMemo(
    () => actions.filter((action) => action.type === 'NO_CONTEXT'),
    [actions]
  );

  const mainActions = useMemo(
    () => actions.filter((action) => action.type === 'CONTEXT_MAIN'),
    [actions]
  );

  const additionalActions = useMemo(
    () => actions.filter((action) => action.type === 'CONTEXT_ADDITIONAL'),
    [actions]
  );

  const openActions = useMemo(
    () => actions.filter((action) => action.type === 'HEADER'),
    [actions]
  );

  const columnActions = useMemo(
    () => actions.filter((action) => action.type === 'COLUMN'),
    [actions]
  );

  // Akcja Reorder
  const reorderAction = actions.find((action) => action.method === 'Reorder');
  const hasReorderAction = !!reorderAction;

  const { mutate: callCommand, isLoading: isMutating } = useCallCommand(
    reorderAction?.method_params?.command_url || '',
    {
      onSuccess: () => {
        refetchTableData();
      }
    }
  );

  const handleReorder = (data: {
    entityId: number;
    beforeEntityId: number | null;
    afterEntityId: number | null;
  }): void => {
    if (reorderAction && callCommand) {
      callCommand(data);
    }
  };

  return {
    noContextActions,
    mainActions,
    additionalActions,
    openActions,
    columnActions,
    handleReorder,
    isReordering: isMutating,
    hasReorderAction
  };
};
